import { FEATURE_IMAGE, SAMPLE_PUBLISHED_DATE } from './studioData';
import type { StudioForm } from './studioData';
import { formatPublished } from '@/lib/format';

type Props = {
  form: StudioForm;
  mode: 'desktop' | 'mobile';
  onModeChange: (mode: 'desktop' | 'mobile') => void;
};

export default function ArticlePreview({ form, mode, onModeChange }: Props) {
  const isMobile = mode === 'mobile';
  const image = form.image || FEATURE_IMAGE;
  const paragraphs = form.body
    .split(/\n{2,}|\r\n\r\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  const toggleClass = (active: boolean) =>
    `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-label uppercase tracking-wide text-[10px] font-semibold whitespace-nowrap cursor-pointer transition-colors ${active ? 'bg-foreground-950 text-background-50' : 'text-foreground-600 hover:text-foreground-900'}`;

  return (
    <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
      <div className="px-5 py-3.5 border-b border-background-200 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-label uppercase tracking-widest text-[11px] text-foreground-500">Live preview</p>
          <p className="text-xs text-foreground-500">Mirrors the public GreenSide News article layout</p>
        </div>
        <div className="flex items-center gap-1 p-1 bg-background-100 border border-background-200 rounded-full">
          <button type="button" onClick={() => onModeChange('desktop')} aria-pressed={mode === 'desktop'} className={toggleClass(mode === 'desktop')}>
            <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-computer-line"></i></span>
            Desktop
          </button>
          <button type="button" onClick={() => onModeChange('mobile')} aria-pressed={mode === 'mobile'} className={toggleClass(mode === 'mobile')}>
            <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-smartphone-line"></i></span>
            Mobile
          </button>
        </div>
      </div>

      <div className={isMobile ? 'bg-background-200/70 p-5 md:p-7 flex justify-center' : ''}>
        <div className={isMobile ? 'w-full max-w-[380px] border-[10px] border-foreground-950 rounded-[34px] overflow-hidden bg-background-50' : 'w-full'}>
          <article>
            <div className={`relative flex items-end ${isMobile ? 'h-56' : 'h-56 md:h-72'}`}>
              {image ? (
                <img src={image} alt={form.headline || 'Article featured image'} title={form.headline || 'Article preview'} className="absolute inset-0 w-full h-full object-cover object-top" />
              ) : (
                <div className="absolute inset-0 bg-foreground-800"></div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/92 via-foreground-950/45 to-foreground-950/25"></div>
              <div className={`relative w-full ${isMobile ? 'p-5' : 'p-5 md:p-7'}`}>
                <span className="inline-block bg-accent-500 text-foreground-950 font-label font-bold uppercase text-[10px] tracking-[0.18em] px-3 py-1.5 rounded-sm">
                  {form.category || 'Category'}
                </span>
                <h3 className={`mt-3 font-heading font-extrabold text-background-50 leading-[1.03] max-w-2xl ${isMobile ? 'text-2xl' : 'text-2xl md:text-4xl'}`}>
                  {form.headline || 'Your headline will appear here'}
                </h3>
                <p className="mt-3 font-label uppercase tracking-widest text-[10px] md:text-xs text-background-200">
                  {form.author || 'GreenSide Newsroom'} &middot; {formatPublished(SAMPLE_PUBLISHED_DATE)}
                </p>
              </div>
            </div>

            <div className={`py-6 ${isMobile ? 'px-5' : 'px-5 md:px-8 py-6 md:py-8'}`}>
              {form.summary && (
                <p className={`text-foreground-700 leading-relaxed font-medium border-l-4 border-accent-500 pl-5 mb-7 ${isMobile ? 'text-lg' : 'text-lg md:text-2xl'}`}>
                  {form.summary}
                </p>
              )}
              {paragraphs.length ? (
                <div className="space-y-5">
                  {paragraphs.map((p, i) => (
                    <p key={i} className={`text-foreground-800 leading-relaxed ${isMobile ? 'text-base' : 'text-base md:text-lg'}`}>{p}</p>
                  ))}
                </div>
              ) : (
                <p className="text-foreground-500 text-sm">
                  The article body renders here with the same typography and spacing as the public story pages.
                </p>
              )}
            </div>
          </article>
        </div>
      </div>

      <div className="px-5 py-4 border-t border-background-200 bg-background-100">
        <p className="font-label uppercase tracking-widest text-[11px] text-foreground-500">Search result preview</p>
        <div className="mt-3">
          <p className="text-xs text-primary-700 truncate">
            {form.slug ? `greensidenews.com/news/${form.slug}` : 'greensidenews.com/news/your-slug'}
          </p>
          <p className="text-base text-foreground-900 font-medium leading-snug mt-0.5 line-clamp-2">
            {form.seoTitle || form.headline || 'Suggested SEO title'}
          </p>
          <p className="text-xs text-foreground-600 mt-1 line-clamp-2">
            {form.metaDescription || 'Suggested meta description for search results.'}
          </p>
        </div>
      </div>
    </div>
  );
}